/**
 * One entry of a form mapping ("Formularzuordnung"). A single form field path is stored as a plain
 * string — exactly as it always was — and only a mapping that combines several form fields into one
 * target (e.g. a name assembled from a separate first and last name field) becomes a list. Keeping
 * the single case a string means every stored mapping and every consumer that has not been touched
 * keeps working unchanged.
 */
export type MappingValue = string | string[] | null | undefined;

/** Normalizes a mapping value into the list of form field paths it points at. */
export const toSourceList = (value: MappingValue): string[] =>
    (Array.isArray(value) ? value : value ? [value] : []).filter(
        (path): path is string => typeof path === 'string' && path !== ''
    );

/** Turns a list of form field paths back into the stored shape: a string for one, a list for more. */
export const fromSourceList = (paths: string[]): string | string[] | null => {
    const cleaned = paths.filter((path) => typeof path === 'string' && path !== '');

    return cleaned.length > 1 ? cleaned : (cleaned[0] ?? null);
};

/** Stable representation of a whole mapping, used to tell a real edit apart from an identical echo. */
export const canonicalizeMapping = (mapping: Record<string, MappingValue> | null): string => {
    if (!mapping) return '';

    // Keys are sorted because their order carries no meaning, the paths within one target are not:
    // they decide in which order the values get combined.
    return JSON.stringify(
        Object.keys(mapping)
            .sort()
            .reduce<Record<string, string[]>>((carry, key) => {
                const sources = toSourceList(mapping[key]);
                if (sources.length) carry[key] = sources;

                return carry;
            }, {})
    );
};
